import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  ExternalLink,
  RefreshCw,
  Image as ImageIcon,
} from "lucide-react";

const CATEGORIES = ["entree", "plat", "dessert", "petit-dejeuner", "boisson", "accompagnement"];
const DIFFICULTIES = ["facile", "moyen", "difficile"];
const PROFILES = ["DT1", "DT2", "pre-diabete", "gestationnel"];

function Section({ title, children }) {
  return (
    <div className="bg-white border border-[hsl(var(--border-light))] rounded-sm p-5 mb-4">
      <p className="overline mb-4">{title}</p>
      {children}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block mb-3">
      <span className="block text-xs uppercase tracking-wider text-[hsl(var(--text-muted))] mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[10px] text-[hsl(var(--text-muted))] mt-1">{hint}</span>}
    </label>
  );
}

const inputClass =
  "w-full text-sm px-3 py-2 bg-white border border-[hsl(var(--border-light))] rounded-sm focus:outline-none focus:border-[hsl(var(--brand-primary))]";

export default function AdminEditRecipe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenImg, setRegenImg] = useState(false);

  useEffect(() => {
    api.get(`/admin/recipes/${id}`)
      .then((r) => setRecipe(r.data))
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Recette introuvable");
        navigate("/admin/recipes");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [id]);

  const set = (patch) => setRecipe((r) => ({ ...r, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: recipe.title,
        subtitle: recipe.subtitle,
        category: recipe.category,
        country: recipe.country,
        region: recipe.region,
        description: recipe.description,
        introduction: recipe.introduction,
        classic_recipe_name: recipe.classic_recipe_name,
        classic_glycemic_index: recipe.classic_glycemic_index,
        ingredients: recipe.ingredients,
        serves: recipe.serves,
        prep_time_min: recipe.prep_time_min,
        cook_time_min: recipe.cook_time_min,
        difficulty: recipe.difficulty,
        steps: recipe.steps,
        glycemic_index: recipe.glycemic_index,
        nutrition: recipe.nutrition,
        profiles: recipe.profiles,
        substitutions: recipe.substitutions,
        diabetes_tips: recipe.diabetes_tips,
        affiliate_products: recipe.affiliate_products,
        keywords: recipe.keywords,
        image_url: recipe.image_url,
        published: recipe.published,
      };
      const r = await api.patch(`/admin/recipes/${id}`, payload);
      setRecipe(r.data);
      toast.success("Recette mise à jour");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(`Erreur: ${detail || e.message || "inconnue"}`);
    } finally {
      setSaving(false);
    }
  };

  const regenerateImage = async () => {
    if (!window.confirm("Régénérer l'image Nano Banana pour cette recette ?")) return;
    setRegenImg(true);
    try {
      const r = await api.post(`/admin/recipes/${id}/regenerate-image`);
      if (r.data.success) {
        toast.success(`Image régénérée (${(r.data.duration_ms / 1000).toFixed(1)}s)`);
        // Refresh recipe to get new image_url
        const fresh = await api.get(`/admin/recipes/${id}`);
        setRecipe(fresh.data);
      } else {
        toast.error(`Échec: ${r.data.error}`);
      }
    } catch (e) {
      toast.error("Erreur régénération");
    } finally {
      setRegenImg(false);
    }
  };

  if (loading) {
    return <p className="text-[hsl(var(--text-muted))]">Chargement…</p>;
  }
  if (!recipe) return null;

  return (
    <div data-testid="admin-edit-recipe-page">
      <div className="flex items-baseline justify-between flex-wrap gap-4 mb-6">
        <div>
          <Link to="/admin/recipes" className="text-xs text-[hsl(var(--text-muted))] hover:text-[hsl(var(--brand-primary))] flex items-center gap-1 mb-2">
            <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Retour aux recettes
          </Link>
          <p className="overline mb-2">Édition · {recipe.category}</p>
          <h1 className="font-serif text-3xl">{recipe.title}</h1>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to={`/recettes/${recipe.slug}`}
            target="_blank"
            data-testid="edit-view-public-btn"
            className="text-sm flex items-center gap-2 px-3 py-2 border border-[hsl(var(--border-light))] rounded-sm hover:bg-[hsl(var(--bg-subtle))]"
          >
            Voir en ligne <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
          </Link>
          <button
            onClick={save}
            disabled={saving}
            data-testid="edit-save-btn"
            className="btn-primary disabled:opacity-50"
          >
            <Save className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT — main content */}
        <div className="lg:col-span-2">
          <Section title="Contenu éditorial">
            <Field label="Titre">
              <input value={recipe.title || ""} onChange={(e) => set({ title: e.target.value })} className={inputClass} data-testid="edit-title" />
            </Field>
            <Field label="Sous-titre">
              <input value={recipe.subtitle || ""} onChange={(e) => set({ subtitle: e.target.value })} className={inputClass} data-testid="edit-subtitle" />
            </Field>
            <Field label="Description courte (meta description, 160 chars max)">
              <textarea rows={3} value={recipe.description || ""} onChange={(e) => set({ description: e.target.value })} className={inputClass} data-testid="edit-description" maxLength={200} />
            </Field>
            <Field label="Introduction (afichée sur la page publique)">
              <textarea rows={5} value={recipe.introduction || ""} onChange={(e) => set({ introduction: e.target.value })} className={inputClass} data-testid="edit-introduction" />
            </Field>
          </Section>

          <Section title="Ingrédients">
            {(recipe.ingredients || []).map((ing, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 mb-2" data-testid={`edit-ingredient-${i}`}>
                <input
                  value={ing.quantity || ""}
                  onChange={(e) => {
                    const next = [...recipe.ingredients];
                    next[i] = { ...ing, quantity: e.target.value };
                    set({ ingredients: next });
                  }}
                  placeholder="Quantité"
                  className={`${inputClass} col-span-3`}
                />
                <input
                  value={ing.name || ""}
                  onChange={(e) => {
                    const next = [...recipe.ingredients];
                    next[i] = { ...ing, name: e.target.value };
                    set({ ingredients: next });
                  }}
                  placeholder="Nom"
                  className={`${inputClass} col-span-6`}
                />
                <input
                  value={ing.note || ""}
                  onChange={(e) => {
                    const next = [...recipe.ingredients];
                    next[i] = { ...ing, note: e.target.value };
                    set({ ingredients: next });
                  }}
                  placeholder="Note"
                  className={`${inputClass} col-span-2`}
                />
                <button
                  type="button"
                  onClick={() => set({ ingredients: recipe.ingredients.filter((_, k) => k !== i) })}
                  className="text-[hsl(var(--brand-primary))] hover:opacity-70"
                  data-testid={`edit-ingredient-delete-${i}`}
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => set({ ingredients: [...(recipe.ingredients || []), { name: "", quantity: "", note: "" }] })}
              className="text-sm text-[hsl(var(--brand-primary))] flex items-center gap-1 mt-2"
              data-testid="edit-ingredient-add"
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> Ajouter un ingrédient
            </button>
          </Section>

          <Section title="Étapes de préparation">
            {(recipe.steps || []).map((step, i) => (
              <div key={i} className="flex gap-2 mb-2" data-testid={`edit-step-${i}`}>
                <span className="text-xs text-[hsl(var(--text-muted))] font-mono pt-2 w-6">{i + 1}.</span>
                <textarea
                  rows={2}
                  value={step}
                  onChange={(e) => {
                    const next = [...recipe.steps];
                    next[i] = e.target.value;
                    set({ steps: next });
                  }}
                  className={`${inputClass} flex-1`}
                />
                <button
                  type="button"
                  onClick={() => set({ steps: recipe.steps.filter((_, k) => k !== i) })}
                  className="text-[hsl(var(--brand-primary))] hover:opacity-70"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => set({ steps: [...(recipe.steps || []), ""] })}
              className="text-sm text-[hsl(var(--brand-primary))] flex items-center gap-1 mt-2"
              data-testid="edit-step-add"
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> Ajouter une étape
            </button>
          </Section>

          <Section title="Conseils diabétiques">
            {(recipe.diabetes_tips || []).map((tip, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <textarea
                  rows={2}
                  value={tip}
                  onChange={(e) => {
                    const next = [...recipe.diabetes_tips];
                    next[i] = e.target.value;
                    set({ diabetes_tips: next });
                  }}
                  className={`${inputClass} flex-1`}
                />
                <button
                  type="button"
                  onClick={() => set({ diabetes_tips: recipe.diabetes_tips.filter((_, k) => k !== i) })}
                  className="text-[hsl(var(--brand-primary))] hover:opacity-70"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => set({ diabetes_tips: [...(recipe.diabetes_tips || []), ""] })}
              className="text-sm text-[hsl(var(--brand-primary))] flex items-center gap-1 mt-2"
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> Ajouter un conseil
            </button>
          </Section>
        </div>

        {/* RIGHT — meta panel */}
        <div>
          <Section title="Image">
            <div className="aspect-[4/3] bg-[hsl(var(--bg-subtle))] rounded-sm overflow-hidden mb-3">
              {recipe.image_url ? (
                <img src={recipe.image_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[hsl(var(--text-muted))] text-xs">
                  Pas d'image
                </div>
              )}
            </div>
            <button
              onClick={regenerateImage}
              disabled={regenImg}
              data-testid="edit-regen-image-btn"
              className="w-full text-sm flex items-center justify-center gap-2 px-3 py-2 border border-[hsl(var(--border-light))] rounded-sm hover:bg-[hsl(var(--bg-subtle))] disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${regenImg ? "animate-spin" : ""}`} strokeWidth={1.5} />
              {regenImg ? "Régénération…" : "Régénérer via Nano Banana"}
            </button>
            <Field label="Ou URL d'image manuelle">
              <input
                value={recipe.image_url && !recipe.image_url.startsWith("data:") ? recipe.image_url : ""}
                onChange={(e) => set({ image_url: e.target.value })}
                placeholder="https://…"
                className={inputClass}
                data-testid="edit-image-url"
              />
            </Field>
          </Section>

          <Section title="Publication">
            <Field label="Statut">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!recipe.published}
                  onChange={(e) => set({ published: e.target.checked })}
                  data-testid="edit-published"
                  className="accent-[hsl(var(--brand-primary))]"
                />
                <span className="text-sm">Publiée sur le site</span>
              </label>
            </Field>
            <Field label="Slug (URL)">
              <input value={recipe.slug || ""} disabled className={`${inputClass} opacity-60`} data-testid="edit-slug" />
            </Field>
          </Section>

          <Section title="Métadonnées">
            <Field label="Catégorie">
              <select value={recipe.category || ""} onChange={(e) => set({ category: e.target.value })} className={inputClass} data-testid="edit-category">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Pays">
              <input value={recipe.country || ""} onChange={(e) => set({ country: e.target.value })} className={inputClass} data-testid="edit-country" />
            </Field>
            <Field label="Région (optionnel)">
              <input value={recipe.region || ""} onChange={(e) => set({ region: e.target.value })} className={inputClass} data-testid="edit-region" />
            </Field>
            <Field label="Nom canonique du plat classique">
              <input value={recipe.classic_recipe_name || ""} onChange={(e) => set({ classic_recipe_name: e.target.value })} className={inputClass} data-testid="edit-classic-name" hint="Ex: 'Bœuf Bourguignon classique' — utilisé par Nano Banana pour l'image" />
            </Field>
            <Field label="Difficulté">
              <select value={recipe.difficulty || ""} onChange={(e) => set({ difficulty: e.target.value })} className={inputClass} data-testid="edit-difficulty">
                {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
          </Section>

          <Section title="Nutrition & IG">
            <Field label="Portions">
              <input type="number" value={recipe.serves || 0} onChange={(e) => set({ serves: parseInt(e.target.value) || 0 })} className={inputClass} data-testid="edit-serves" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Prép (min)">
                <input type="number" value={recipe.prep_time_min || 0} onChange={(e) => set({ prep_time_min: parseInt(e.target.value) || 0 })} className={inputClass} data-testid="edit-prep-time" />
              </Field>
              <Field label="Cuisson (min)">
                <input type="number" value={recipe.cook_time_min || 0} onChange={(e) => set({ cook_time_min: parseInt(e.target.value) || 0 })} className={inputClass} data-testid="edit-cook-time" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="IG version diabète">
                <input type="number" value={recipe.glycemic_index || 0} onChange={(e) => set({ glycemic_index: parseInt(e.target.value) || 0 })} className={inputClass} data-testid="edit-gi" />
              </Field>
              <Field label="IG classique">
                <input type="number" value={recipe.classic_glycemic_index || 0} onChange={(e) => set({ classic_glycemic_index: parseInt(e.target.value) || 0 })} className={inputClass} data-testid="edit-classic-gi" />
              </Field>
            </div>
          </Section>

          <Section title="SEO">
            <Field label="Mots-clés (séparés par virgule)">
              <textarea
                rows={3}
                value={(recipe.keywords || []).join(", ")}
                onChange={(e) => set({ keywords: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                className={inputClass}
                data-testid="edit-keywords"
              />
            </Field>
            <Field label="Profils diabète">
              <div className="flex gap-2 flex-wrap">
                {PROFILES.map((p) => {
                  const on = (recipe.profiles || []).includes(p);
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => {
                        const cur = recipe.profiles || [];
                        set({ profiles: on ? cur.filter((x) => x !== p) : [...cur, p] });
                      }}
                      className={`text-xs px-3 py-1.5 rounded-sm border ${on ? "bg-[hsl(var(--brand-primary))] text-white border-[hsl(var(--brand-primary))]" : "bg-white text-[hsl(var(--text-secondary))] border-[hsl(var(--border-light))]"}`}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
            </Field>
          </Section>
        </div>
      </div>

      {/* STICKY BOTTOM SAVE */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          data-testid="edit-save-bottom-btn"
          className="btn-primary disabled:opacity-50"
        >
          <Save className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Enregistrement…" : "Enregistrer les modifications"}
        </button>
      </div>
    </div>
  );
}
